import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, Menu, X, House, UserCircle, Eye, Bell } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { FEATURES, hasFeature } from "../lib/perms";
import { NotificationBell } from "./NotificationBell";
import { NoticeSender } from "./NoticeSender";
import { QuickExpenseFab } from "./QuickExpenseFab";

export const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const items = FEATURES.filter((f) => hasFeature(user, f.key));

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
    const impersonating = typeof window !== "undefined" && !!sessionStorage.getItem("imp_token");
    const endImpersonation = async () => {
        await logout();
        navigate("/login");
        try { window.close(); } catch (e) { /* ignore */ }
    };

    const NavItems = () => (
        <nav className="flex flex-col gap-1">
            {items.map((it) => (
                <NavLink
                    key={it.path}
                    to={it.path}
                    end={it.path === "/"}
                    data-testid={`nav-${it.key}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                        `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-muted"}`
                    }
                >
                    <it.icon style={{ width: 18, height: 18 }} />
                    {it.label}
                </NavLink>
            ))}
            <NavLink
                to="/avisos"
                data-testid="nav-avisos"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-muted"}`
                }
            >
                <Bell style={{ width: 18, height: 18 }} />
                Central de Avisos
            </NavLink>
        </nav>
    );

    return (
        <div className="min-h-screen bg-background md:flex">
            {/* Sidebar desktop */}
            <aside className="hidden w-72 shrink-0 flex-col border-r border-border bg-white p-6 md:flex">
                <div className="mb-8">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                            <House className="h-5 w-5" />
                        </div>
                        <p className="font-heading text-lg font-bold leading-tight text-primary">Casa Financeiro</p>
                    </div>
                    <div className="mt-3 flex items-center gap-1">
                        {user?.role === "gestor" && <NoticeSender />}
                        <NotificationBell />
                    </div>
                </div>
                <NavItems />
                <div className="mt-auto flex items-center gap-3 rounded-2xl border border-border p-3">
                    <NavLink to="/perfil" data-testid="nav-perfil" onClick={() => setOpen(false)} className="flex min-w-0 flex-1 items-center gap-3">
                        {user?.picture ? (
                            <img src={user.picture} alt="" className="h-10 w-10 rounded-full object-cover" />
                        ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-sm font-semibold text-white">{initials}</div>
                        )}
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold">{user?.name}</p>
                            <p className="truncate text-xs capitalize text-muted-foreground">{user?.is_master ? "Master Geral" : `${user?.role} · ${user?.nivel}`}</p>
                        </div>
                    </NavLink>
                    <button onClick={handleLogout} data-testid="logout-btn" className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-expense">
                        <LogOut className="h-4 w-4" />
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="flex items-center justify-between border-b border-border bg-white p-4 md:hidden">
                <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                        <House className="h-4 w-4" />
                    </div>
                    <span className="font-heading font-bold text-primary">Casa Financeiro</span>
                </div>
                <div className="flex items-center gap-1">
                    {user?.role === "gestor" && <NoticeSender />}
                    <NotificationBell />
                    <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="rounded-lg p-2 hover:bg-muted">
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="border-b border-border bg-white p-4 md:hidden">
                    <NavItems />
                    <NavLink to="/perfil" data-testid="nav-perfil-mobile" onClick={() => setOpen(false)} className="mt-1 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-foreground/70 hover:bg-muted">
                        <UserCircle className="h-4 w-4" /> Meu Perfil
                    </NavLink>
                    <button onClick={handleLogout} data-testid="logout-btn-mobile" className="mt-1 flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-expense hover:bg-muted">
                        <LogOut className="h-4 w-4" /> Sair
                    </button>
                </div>
            )}

            <main className="flex-1 p-5 pb-24 md:p-10 md:pb-24">
                <div className="mx-auto max-w-6xl">
                    {impersonating && (
                        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-secondary bg-secondary/30 p-4 text-sm text-secondary-foreground sm:flex-row sm:items-center sm:justify-between" data-testid="impersonation-banner">
                            <span className="flex items-center gap-2 font-medium"><Eye className="h-4 w-4" /> Você está acessando como <strong>{user?.name}</strong> (modo master).</span>
                            <button onClick={endImpersonation} data-testid="end-impersonation-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2 text-xs font-medium text-white">Encerrar acesso</button>
                        </div>
                    )}
                    {children}
                </div>
            </main>

            <QuickExpenseFab />
        </div>
    );
};

export const PageHeader = ({ overline, title, description, action }) => (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
            {overline && <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{overline}</p>}
            <h1 className="font-heading text-3xl font-light tracking-tight text-foreground sm:text-4xl">{title}</h1>
            {description && <p className="mt-2 max-w-xl text-base leading-relaxed text-muted-foreground">{description}</p>}
        </div>
        {action}
    </div>
);
